import { MutationButton } from "@Light/components/page/MutationButton";
import { TermsAcceptance } from "@Light/components/rateOffer";
import { AcceptedBy } from "@Light/services/lightTypes";
import { usePlan } from "@Light/utils/plan";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { isRateGoneError } from "../acceptPlanError";
import { EnrollHelpButtonFooter } from "../EnrollHelpButtonFooter";
import { useEnrollment } from "../flow/context";
import { requirementValue } from "../flow/screen";
import { EnrollRoute } from "../routes";
import { useEnrollmentUtilityConfiguration } from "../useEnrollmentUtilityConfiguration";
import { RateExpiredSheet } from "./RateExpiredSheet";

export const Footer = () => {
  const { group } = usePlan();
  const { saveAndFinalize, saveAndFinalizeMutation, requirements, navigate } =
    useEnrollment();
  const acceptance = requirementValue(requirements.acceptance);
  const { disclosureTerms, makeAuthorizationTerms } =
    useEnrollmentUtilityConfiguration();

  const rateExpired =
    Boolean(saveAndFinalizeMutation.isError) &&
    isRateGoneError(saveAndFinalizeMutation.error as FetchBaseQueryError);

  const [tosAccepted, setTosAccepted] = useState(false);
  const [authorizationAccepted, setAuthorizationAccepted] = useState(false);
  const [isTosError, setIsTosError] = useState(false);
  const [isAuthorizationError, setIsAuthorizationError] = useState(false);

  const onChangeTos = (accepted: boolean) => {
    setTosAccepted(accepted);
    if (accepted) setIsTosError(false);
  };

  const onChangeAuthorization = (accepted: boolean) => {
    setAuthorizationAccepted(accepted);
    if (accepted) setIsAuthorizationError(false);
  };

  const onComplete = () => {
    const tosMissing = !!disclosureTerms && !tosAccepted;
    const authorizationMissing =
      !!makeAuthorizationTerms && !authorizationAccepted;
    if (tosMissing || authorizationMissing) {
      setIsTosError(tosMissing);
      setIsAuthorizationError(authorizationMissing);
      return;
    }

    void saveAndFinalize({
      acceptance: {
        rate_uuid: acceptance.rate_id,
        // Only mark true if terms were actually rendered
        terms_accepted: makeAuthorizationTerms
          ? true
          : !!acceptance.accepted_at,
        accepted_by: AcceptedBy.Customer,
      },
    });
  };

  return (
    <EnrollHelpButtonFooter>
      <div className="flex flex-col gap-4">
        {disclosureTerms ? (
          <TermsAcceptance
            checked={tosAccepted}
            isError={isTosError}
            onChange={onChangeTos}
          >
            {disclosureTerms}
          </TermsAcceptance>
        ) : null}
        {makeAuthorizationTerms ? (
          <TermsAcceptance
            checked={authorizationAccepted}
            isError={isAuthorizationError}
            onChange={onChangeAuthorization}
          >
            {makeAuthorizationTerms(group.name)}
          </TermsAcceptance>
        ) : null}
        <MutationButton
          size="lg"
          mutate={onComplete}
          mutation={saveAndFinalizeMutation}
          mutateButtonText="Complete enrollment"
          errorMessage={
            rateExpired
              ? undefined
              : "Failed finalizing enrollment. Please try again later."
          }
          data-testid="enroll-submit"
        >
          Complete enrollment
        </MutationButton>
      </div>
      <RateExpiredSheet
        open={rateExpired}
        onReviewOffer={() => {
          saveAndFinalizeMutation.reset?.();
          navigate(EnrollRoute.ConfirmRate);
        }}
      />
    </EnrollHelpButtonFooter>
  );
};
